import { RecoilRoot } from "recoil";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing";
import React, { useEffect, useMemo, useRef } from "react";
import { createStackNavigator, StackScreenProps } from "@react-navigation/stack";
import { AssetStatus, ContractSignatureStatus, ProfileType } from "../../../__generated__/graphql";

import { GET_CONTRACT } from "../../../graphql/core";
import { buildAsset, buildContract, buildMissionInfo, buildUser } from "../../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedResponse } from "@apollo/client/testing/core";
import CreateMissionPreSign from "../../../screens/create-mission/CreateMissionPreSign";
import { useNavigation } from "@react-navigation/native";
import { Button, View } from "react-native";
import Callout from "../../../ui_atoms/Callout";
import { MockedProfileSwitcherManager } from "../../../contexts/ProfileSwitcherManager";
import { GET_ASSET } from "../../../graphql/asset";

type WithNavigationProps = {
    navigation: {
        reset: (x: object) => void;
    };
};

const HomeFakeScreen: React.FC<WithNavigationProps> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Sign contract"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "CreateMissionPreSign",
                                params: { contractId: "test-contract" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

type FakePreviewScreenProps = WithNavigationProps & {
    route: {
        params: {
            contractId: string;
        };
    };
};

const FakePreviewScreen: React.FC<FakePreviewScreenProps> = ({ route, navigation }) => {
    return (
        <View>
            <Callout markdown={`PDF Preview ${route.params.contractId}`} />
            <Button
                title="Sign contract"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [{ name: "Home" }],
                    });
                }}
            />
        </View>
    );
};
type StoryStackParams = {
    CreateMissionPreSign: { contractId: string };
    Home: undefined;
    ContractPreviewScreen: { contractId: string };
};

type StoryStackNavigatorParams = StackScreenProps<StoryStackParams>;

const Stack = createStackNavigator<StoryStackParams>();

const StoryComponent = ({ state }: { state: ContractSignatureStatus }) => {
    const user = useMemo(buildUser, []);
    const assetStatusRef = useRef(AssetStatus.Pending);
    const mock = useMemo(
        () =>
            Object.values(ContractSignatureStatus)
                .reduce((acc, state) => {
                    const contractId = `test-contract-${state}`;
                    acc.push({
                        request: {
                            query: GET_CONTRACT,
                            variables: {
                                id: contractId,
                            },
                        },
                        newData: () => {
                            assetStatusRef.current = AssetStatus.Pending;
                            setTimeout(() => {
                                assetStatusRef.current = AssetStatus.Ready;
                            }, 8000);
                            return {
                                data: {
                                    getContract: {
                                        ...buildContract({
                                            id: contractId,
                                            signatureStatus: state,
                                        }),
                                        ...{ missionInfo: buildMissionInfo() },
                                    },
                                },
                            };
                        },
                    });
                    return acc;
                }, [] as MockedResponse[])
                .concat([
                    {
                        variableMatcher: () => true,
                        request: {
                            query: GET_ASSET,
                        },
                        newData: () => {
                            return {
                                data: {
                                    getAsset: buildAsset({
                                        status: assetStatusRef.current,
                                    }),
                                },
                            };
                        },
                    },
                ]),
        [],
    );

    const navigation = useNavigation<StoryStackNavigatorParams["navigation"]>();

    useEffect(() => {
        navigation.navigate("CreateMissionPreSign", { contractId: `test-contract-${state}` });
    }, [state, navigation]);

    return (
        <RecoilRoot>
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager profileType={ProfileType.Client}>
                    <MockedProvider mocks={mock}>
                        <Stack.Navigator initialRouteName="CreateMissionPreSign">
                            <Stack.Screen
                                name="CreateMissionPreSign"
                                component={CreateMissionPreSign}
                                initialParams={{ contractId: `test-contract-${state}` }}
                                options={{
                                    gestureEnabled: false,
                                    headerShown: true,
                                    title: "",
                                    headerBackTitle: "Back",
                                    headerBackButtonDisplayMode: "default",
                                }}
                            />
                            <Stack.Screen name="Home" component={HomeFakeScreen} />
                            <Stack.Screen
                                name="ContractPreviewScreen"
                                /* @ts-expect-error Stack Component need a complex navigation handler */
                                component={FakePreviewScreen}
                            />
                        </Stack.Navigator>
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
        </RecoilRoot>
    );
};

const meta = {
    title: "CreateMission/Pre Sign",
    component: StoryComponent,
    argTypes: {
        state: {
            options: Object.values(ContractSignatureStatus),
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const NeedSelfSign: Story = {
    name: "NeedSelfSign",
    args: {
        state: ContractSignatureStatus.NeedSelfSign,
    },
};

export const Success: Story = {
    name: "Success",
    args: {
        state: ContractSignatureStatus.Success,
    },
};

export const WaitingContract: Story = {
    name: "WaitingContract",
    args: {
        state: ContractSignatureStatus.WaitingContract,
    },
};
